import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, enableNetwork, disableNetwork, enableIndexedDbPersistence, collection, doc, getDocs, writeBatch, setDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, getDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { isDevToolsGateOpen } from './utils/devToolsGate';

// Debug: Log ALL env vars BEFORE config creation
if (isDevToolsGateOpen()) {
  console.log('RAW ENV VARS:', {
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
    VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    ALL_ENV_KEYS: Object.keys(import.meta.env)
  });
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Debug: Log loaded config
if (isDevToolsGateOpen()) {
  console.log('Firebase Config Loaded:', {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    apiKey: firebaseConfig.apiKey?.substring(0, 20) + '...'
  });
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Export Firestore functions for convenience
export { collection, doc, getDocs, writeBatch, setDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, getDoc, serverTimestamp, onSnapshot };

// Initialize Cloud Functions and get a reference to the service
export const functions = getFunctions(app, 'africa-south1');
if (isDevToolsGateOpen()) {
  console.log('Firebase Functions initialized - region: africa-south1');
}

// Initialize Firebase Storage and get a reference to the service
export const storage = getStorage(app);

// Export httpsCallable for use in other files
export { httpsCallable };

// Enable offline persistence for better performance and reduced reads
// This will cache data locally in IndexedDB, reducing Firebase read costs by 80-90%
// Page refreshes and navigation will use cached data first, then sync changes
// TEMPORARILY DISABLED - Testing CORS fix
// DISABLED in development - causes connection corruption with Vite HMR
if (false && !import.meta.env.DEV) {
  enableIndexedDbPersistence(db).catch((error) => {
    if (error.code === 'failed-precondition') {
      // Multiple tabs open, persistence can only be enabled in one tab at a time
      // This is expected behavior when multiple tabs are open - ignore the error
      console.warn('[Firebase] Persistence already enabled in another tab - using memory cache');
    } else if (error.code === 'unimplemented') {
      // The current browser doesn't support persistence
      console.warn('[Firebase] Browser does not support offline persistence');
    } else {
      console.error('[Firebase] Failed to enable persistence:', error);
    }
  });
  console.log('[Firebase] Offline persistence enabled - reads will be cached locally');
} else {
  if (isDevToolsGateOpen()) {
    console.log('[Firebase] Offline persistence DISABLED - Testing CORS fix');
  }
}

// Connect to emulators in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  try {
    const emulatorHost = import.meta.env.VITE_FIREBASE_EMULATOR_HOST || 'localhost';
    const authEmulatorPort = Number(
      import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT || 9099
    );
    const firestoreEmulatorPort = Number(
      import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080
    );
    connectAuthEmulator(
      auth,
      `http://${emulatorHost}:${authEmulatorPort}`,
      { disableWarnings: true }
    );
    connectFirestoreEmulator(db, emulatorHost, firestoreEmulatorPort);
    const functionsPort = Number(
      import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001
    );
    connectFunctionsEmulator(functions, emulatorHost, functionsPort);
    connectStorageEmulator(storage, 'localhost', 9199);
  } catch (error) {
    // Emulators already connected
  }
}

// Network connectivity handler for Firestore
if (typeof window !== 'undefined') {
  let reconnectTimeout;

  const handleOnline = async () => {
    clearTimeout(reconnectTimeout);
    try {
      await enableNetwork(db);
      console.log('[Firebase] Network re-enabled');
    } catch (error) {
      console.warn('[Firebase] Failed to enable network:', error.message);
    }
  };

  const handleOffline = async () => {
    clearTimeout(reconnectTimeout);
    // Wait a bit before disabling to avoid rapid toggles
    reconnectTimeout = setTimeout(async () => {
      try {
        await disableNetwork(db);
        console.log('[Firebase] Network disabled - offline mode');
      } catch (error) {
        console.warn('[Firebase] Failed to disable network:', error.message);
      }
    }, 3000);
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
}

// Install aggressive read protection
// TEMPORARILY DISABLED FOR DEBUGGING
// if (!import.meta.env.DEV || import.meta.env.VITE_DISABLE_PROTECTION !== 'true') {
//   installProtection();
//   console.log('🛡️ Firebase read protection enabled - 5s cache, 1000 reads/min limit');
// }
if (isDevToolsGateOpen()) {
  console.warn('READ PROTECTION DISABLED FOR DEBUGGING');
}

export default app;
